import {
	PLAYER,
	PHASE,
	SIMPLE_AWARDS,
	AWARD_NAMES,
} from "../../../common/constants.ts";
import { idb } from "../../db/index.ts";
import {
	g,
	defaultGameAttributes,
	helpers,
	logEvent,
	orderTeams,
} from "../../util/index.ts";
import type {
	Conditions,
	Player,
	PlayerFiltered,
	TeamFiltered,
} from "../../../common/types.ts";
import { POS_NUMBERS_INVERSE } from "../../../common/constants.baseball.ts";
import season from "./index.ts";
import addAward from "../player/addAward.ts";
import { bySport, isSport } from "../../../common/sportFunctions.ts";

export type AwardsByPlayer = {
	pid: number;
	tid: number;
	name: string;
	type: string;
}[];

export type GetTopPlayersOptions = {
	allowNone?: boolean;
	amount?: number;
	filter?: (a: PlayerFiltered) => boolean;
	score: (a: PlayerFiltered) => number;
};

const getProcessedPlayers = async (
	playersAll: Player[],
	season: number,
	playoffs?: boolean,
) => {
	let players = await idb.getCopies.playersPlus(playersAll, {
		attrs: [
			"pid",
			"name",
			"firstName",
			"lastName",
			"tid",
			"abbrev",
			"draft",
			"injury",
			"born",
			"watch",
		],
		ratings: ["pos", "season", "ovr", "dovr", "pot", "skills"],
		stats: bySport({
			baseball: [
				"keyStats",
				"gpPit",
				"gsPit",
				"w",
				"l",
				"sv",
				"era",
				"ip",
				"war",
				"rpit",
				"season",
				"abbrev",
				"tid",
				"jerseyNumber",

				// For all-offense/defense teams
				"rbat",
				"rbr",
				"rfld",

				// For position determination
				"gpF",

				// For season leaders
				"hr",
				"rbi",
				"r",
				"sb",
				"bb",
				"soPit",
			],
			basketball: [
				"gp",
				"gs",
				"min",
				"pts",
				"trb",
				"ast",
				"blk",
				"stl",
				"per",
				"ewa",
				"ws",
				"dws",
				"vorp",
				"ws48",
				"season",
				"abbrev",
				"tid",
				"jerseyNumber",
			],
			football: [
				"keyStats",
				"pntYds",
				"fg",
				"krTD",
				"krYds",
				"prTD",
				"prYds",
				"pssYds",
				"pssTD",
				"pssInt",
				"rusYds",
				"rusTD",
				"recYds",
				"recTD",
				"fmbLost",
				"prTD",
				"krTD",
				"ydsFromScrimmage",
				"season",
				"abbrev",
				"tid",
				"jerseyNumber",
				"defIntTD",
				"defFmbTD",
				"defSft",
				"defSk",
				"defInt",
				"defPssDef",
				"defFmbFrc",
				"defFmbRec",
				"defTckSolo",
				"defTckAst",
				"defTckLoss",
				"totTD",
				"pbw",
				"pba",
				"pbwr",
				"rbw",
				"rba",
				"rbwr",
			],
			hockey: [
				"keyStats",
				"gpGoalie",
				"g",
				"a",
				"pts",
				"hit",
				"tk",
				"gaa",
				"svPct",
				"ops",
				"dps",
				"gps",
				"ps",
				"season",
				"abbrev",
				"tid",
				"jerseyNumber",
			],
		}),
		playoffs,
		regularSeason: !playoffs,
		fuzz: true,
		mergeStats: "totOnly",
	});

	// Only keep players who actually have a stats entry for the latest season
	players = players.filter((p) =>
		p.stats.some((ps: any) => ps.season === season),
	);

	// This can happen if there are 0 games in the regular season - in that case, might as well look for playoff stats too
	if (players.length === 0 && !playoffs) {
		return getProcessedPlayers(playersAll, season, true);
	}

	return players;
};

const getPlayers = async (season: number): Promise<PlayerFiltered[]> => {
	let playersAll;
	if (g.get("season") === season && g.get("phase") <= PHASE.PLAYOFFS) {
		playersAll = await idb.cache.players.indexGetAll("playersByTid", [
			PLAYER.FREE_AGENT,
			Infinity,
		]);
	} else {
		playersAll = await idb.getCopies.players(
			{
				activeSeason: season,
			},
			"noCopyCache",
		);
	}

	const players = await getProcessedPlayers(playersAll, season);

	// Add winp, for later
	const teamSeasons = await idb.getCopies.teamSeasons(
		{
			season,
		},
		"noCopyCache",
	);
	const teamInfos: Record<
		number,
		{
			gp: number;
			winp: number;
		}
	> = {};
	for (const teamSeason of teamSeasons) {
		teamInfos[teamSeason.tid] = {
			gp: helpers.getTeamSeasonGp(teamSeason),
			winp: helpers.calcWinp(teamSeason),
		};
	}

	// For convenience later
	for (const p of players) {
		p.currentStats = p.stats.at(-1);
		for (let i = p.stats.length - 1; i >= 0; i--) {
			if (p.stats[i].season === season) {
				p.currentStats = p.stats[i];
				break;
			}
		}

		p.pos = p.ratings.at(-1).pos;
		if (isSport("baseball")) {
			// Overwrite position with actual position played
			const gpF = (p.currentStats.gpF as (number | undefined)[]).map((gp) =>
				gp === undefined ? 0 : gp,
			);
			let maxGP = 0; // Start at 0 rather than -Infinity because we're not interested in positions with 0 games played
			let maxIndex;
			for (const [i, gp] of gpF.entries()) {
				if (gp > maxGP) {
					maxGP = gp;
					maxIndex = i;
				}
			}

			if (maxIndex !== undefined) {
				p.pos = (POS_NUMBERS_INVERSE as any)[maxIndex + 1];
			}
		}

		// Otherwise it's always the current season
		p.age = season - p.born.year;

		// Player somehow on an inactive team needs this fallback, should only happen in a weird custom roster
		p.teamInfo = teamInfos[p.currentStats.tid] ?? {
			gp: 0,
			winp: 0,
		};
	}

	// Add fracWS for basketball current season
	if (isSport("basketball")) {
		const totalWS: Record<number, number> = {};
		for (const p of players) {
			if (totalWS[p.currentStats.tid] === undefined) {
				totalWS[p.currentStats.tid] = 0;
			}
			totalWS[p.currentStats.tid] += p.currentStats.ws;
		}

		for (const p of players) {
			p.currentStats.fracWS = Math.min(
				// Inner max is to handle negative totalWS
				p.currentStats.ws / Math.max(totalWS[p.currentStats.tid]!, 1),

				// In the rare case that a team has very low or even negative WS, don't let anybody have a crazy high fracWS
				0.8,
			);
		}
	}

	return players;
};

const teamAwards = async (
	teamsUnsorted: TeamFiltered<
		["tid"],
		[
			"winp",
			"pts",
			"won",
			"lost",
			"tied",
			"otl",
			"wonDiv",
			"lostDiv",
			"tiedDiv",
			"otlDiv",
			"wonConf",
			"lostConf",
			"tiedConf",
			"otlConf",
			"cid",
			"did",
			"abbrev",
			"region",
			"name",
		],
		["pts", "oppPts", "gp"],
		number
	>[],
) => {
	const teams = await orderTeams(teamsUnsorted, teamsUnsorted);

	if (!teams[0]) {
		throw new Error("No teams found");
	}

	const ties = season.hasTies("current");

	const bestRecord = {
		tid: teams[0].tid,
		abbrev: teams[0].seasonAttrs.abbrev,
		region: teams[0].seasonAttrs.region,
		name: teams[0].seasonAttrs.name,
		won: teams[0].seasonAttrs.won,
		lost: teams[0].seasonAttrs.lost,
		tied: ties ? teams[0].seasonAttrs.tied : undefined,
		otl: g.get("otl", "current") ? teams[0].seasonAttrs.otl : undefined,
	};
	const bestRecordConfs = await Promise.all(
		g.get("confs", "current").map(async (c) => {
			const teamsConf = await orderTeams(
				teams.filter((t2) => t2.seasonAttrs.cid === c.cid),
				teams,
			);
			const t = teamsConf[0];

			if (!t) {
				return;
			}

			return {
				tid: t.tid,
				abbrev: t.seasonAttrs.abbrev,
				region: t.seasonAttrs.region,
				name: t.seasonAttrs.name,
				won: t.seasonAttrs.won,
				lost: t.seasonAttrs.lost,
				tied: ties ? t.seasonAttrs.tied : undefined,
				otl: g.get("otl", "current") ? t.seasonAttrs.otl : undefined,
			};
		}),
	);

	return {
		bestRecord,
		bestRecordConfs,
	};
};

const leagueLeaders = (
	players: PlayerFiltered[],
	categories: {
		name: string;
		stat: string;
		minValue: number;
	}[],
	awardsByPlayer: AwardsByPlayer,
) => {
	const numGames = g.get("numGames");
	const factor =
		(numGames / defaultGameAttributes.numGames[0].value) *
		helpers.quarterLengthFactor(); // To handle changes in number of games and playing time

	for (const cat of categories) {
		let leaders = [];
		let leaderValue = -Infinity;
		for (const p of players) {
			const actualValue = p.currentStats[cat.stat];

			// In basketball, everything except gp is a per-game average, so we need to scale them by games played to check against minValue. In other sports, this whole check is unneccessary currently, because the stats are season totals not per game averages.
			let playerValue;
			if (!isSport("basketball")) {
				playerValue = actualValue;
			} else {
				playerValue = actualValue * p.currentStats.gp;
			}
			if (
				playerValue >= cat.minValue * factor ||
				p.currentStats.gp >= 0.85 * numGames
			) {
				if (actualValue > leaderValue) {
					leaders = [p];
					leaderValue = actualValue;
				} else if (actualValue === leaderValue) {
					leaders.push(p);
				}
			}
		}

		for (const p of leaders) {
			awardsByPlayer.push({
				pid: p.pid,
				tid: p.tid,
				name: p.name,
				type: cat.name,
			});
		}
	}
};

const getTopPlayers = (
	{ amount, filter, score }: GetTopPlayersOptions,
	playersUnsorted: PlayerFiltered[],
): PlayerFiltered[] => {
	if (playersUnsorted.length === 0) {
		return [];
	}

	const actualFilter = filter ?? (() => true);
	const actualAmount = amount ?? 1;
	const cache: Map<number, number> = new Map();
	const players = playersUnsorted.filter(actualFilter).sort((a, b) => {
		let aScore = cache.get(a.pid);

		if (aScore === undefined) {
			aScore = score(a);
			cache.set(a.pid, aScore);
		}

		let bScore = cache.get(b.pid);

		if (bScore === undefined) {
			bScore = score(b);
			cache.set(b.pid, bScore);
		}

		return bScore - aScore;
	});

	// If all players are filtered out above (like MIP initial year), then this will return an empty array
	return players.slice(0, actualAmount);
};

const saveAwardsByPlayer = async (
	awardsByPlayer: AwardsByPlayer,
	conditions: Conditions,
	season: number = g.get("season"),
	logEvents: boolean = true,
	allStarGID?: number,
) => {
	if (awardsByPlayer.length === 0) {
		return;
	}

	// None of this stuff needs to block, it's just notifications
	for (const p of awardsByPlayer) {
		let text = `<a href="${helpers.leagueUrl(["player", p.pid])}">${
			p.name
		}</a> (<a href="${helpers.leagueUrl([
			"roster",
			`${g.get("teamInfoCache")[p.tid]?.abbrev}_${p.tid}`,
			g.get("season"),
		])}">${g.get("teamInfoCache")[p.tid]?.abbrev}</a>) `;
		let score;

		if (p.type.includes("Team")) {
			text += `made the ${p.type}.`;
			score = 10;
		} else if (p.type.includes("Leader")) {
			text += `led the league in ${p.type
				.replace("League ", "")
				.replace(" Leader", "")
				.toLowerCase()}.`;
			score = 10;
		} else if (p.type === "All-Star") {
			text += "made the All-Star team.";
			score = 10;
		} else if (p.type === "All-Star MVP") {
			text += `won the <a href="${helpers.leagueUrl([
				"game_log",
				"special",
				season,
				allStarGID,
			])}">All-Star MVP</a> award.`;
			score = 10;
		} else if (p.type === "Slam Dunk Contest Winner") {
			text += "won the slam dunk contest.";
			score = 10;
		} else if (p.type === "Three-Point Contest Winner") {
			text += "won the three-point contest.";
			score = 10;
		} else {
			text += `won the ${p.type} award.`;
			score = 20;
		}

		if (logEvents) {
			logEvent(
				{
					type: "award",
					text,
					showNotification: false,
					pids: [p.pid],
					tids: [p.tid],
					score,
				},
				conditions,
			);
		}
	}
	const pids = Array.from(
		new Set(awardsByPlayer.map((award) => award.pid)),
	).filter((x) => x != undefined);
	for (const pid of pids) {
		let p = await idb.cache.players.get(pid);
		if (!p) {
			p = (await idb.getCopy.players(
				{
					pid,
				},
				"noCopyCache",
			)) as any;
		}

		if (p && pid != undefined) {
			for (const awardByPlayer of awardsByPlayer) {
				if (awardByPlayer.pid === pid) {
					addAward(p, {
						season,
						type: awardByPlayer.type,
					});
				}
			}
			await idb.cache.players.put(p);
		}
	}
};

const deleteAwardsByPlayer = async (
	awardsByPlayer: {
		pid: number;
		type: string;
	}[],
	season: number,
) => {
	if (awardsByPlayer.length === 0) {
		return;
	}

	const pids = Array.from(new Set(awardsByPlayer.map((award) => award.pid)));
	const players = await idb.getCopies.players(
		{
			pids,
		},
		"noCopyCache",
	);
	for (const p of players) {
		const typesToDelete = awardsByPlayer
			.filter((award) => award.pid === p.pid)
			.map((award) => award.type);
		p.awards = p.awards.filter(
			(award) => award.season != season || !typesToDelete.includes(award.type),
		);
		await idb.cache.players.put(p);
	}
};

const addSimpleAndTeamAwardsToAwardsByPlayer = (
	awards: any,
	awardsByPlayer: AwardsByPlayer,
) => {
	for (const key of SIMPLE_AWARDS) {
		const type = AWARD_NAMES[key] as string;
		const award = awards[key];

		if (!award) {
			// e.g. MIP in first season
			continue;
		}

		const { pid, tid, name } = award;
		awardsByPlayer.push({
			pid,
			tid,
			name,
			type,
		});
	}
	const awardsTeams = bySport({
		baseball: ["allRookie", "allOffense", "allDefense"] as const,
		basketball: ["allRookie", "allLeague", "allDefensive", "sfmvp"] as const,
		football: ["allRookie", "allLeague"] as const,
		hockey: ["allRookie", "allLeague"] as const,
	});
	for (const key of awardsTeams) {
		if (!awards[key]) {
			continue;
		}

		const type = AWARD_NAMES[key] as string;

		if (key === "allRookie" || key === "sfmvp" || isSport("baseball")) {
			for (const p of awards[key]) {
				if (p) {
					const { pid, tid, name } = p;
					awardsByPlayer.push({
						pid,
						tid,
						name,
						type,
					});
				}
			}
		} else {
			for (const level of awards[key]) {
				for (const p of level.players) {
					if (p) {
						const { pid, tid, name } = p;
						awardsByPlayer.push({
							pid,
							tid,
							name,
							type: `${level.title} ${type}`,
						});
					}
				}
			}
		}
	}
};

export {
	getPlayers,
	getTopPlayers,
	leagueLeaders,
	deleteAwardsByPlayer,
	saveAwardsByPlayer,
	addSimpleAndTeamAwardsToAwardsByPlayer,
	teamAwards,
};
